import React, { useCallback, useContext, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import StyledTable from '../../../../../shared/components/v3/Table/StyledTable/StyledTable';
import TablePaginationV3 from '../../../../../shared/components/v3/TablePagination/TablePagination';
import { REQUEST_DETAIL_ROUTE } from '../../../../../shared/constants/auth';
import { DEFAULT_PAGINATOR_OPTIONS_V3, DEFAULT_PAGINATOR_STATE_V3 } from '../../../../../shared/constants/constants';
import { CLICK_ACTION, DETAIL_REQUESTS, SECTION_REQUESTS } from '../../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { FLEXIBLE_REMUNERATION_REQUEST_TABLE_COLUMNS } from '../../../../../shared/handlers/tableColumns';
import { useIsMounted } from '../../../../../shared/hooks/useIsMounted';
import { IFlexibleRemunerationRequest } from '../../../../../shared/interfaces/company';
import Company from '../../../../../shared/services/api/companies/companies';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_REQUESTS } from '../../../../../shared/translations/requests';
import { sortArray } from '../../../../../shared/utils/utils';
import RequestFilter from '../RequestFilter/RequestFilter';

const RequestTable = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [requests, setRequests] = useState<IFlexibleRemunerationRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<IFlexibleRemunerationRequest[]>([]);
  const [currentPageData, setCurrentPageData] = useState<IFlexibleRemunerationRequest[]>([]);
  const [error, setError] = useState(false);
  const [activeRow, setActiveRow] = useState<string | undefined>(undefined);
  const [paginatorState, setPaginatorState] = useState<number>(DEFAULT_PAGINATOR_STATE_V3);
  const [actualPage, setActualPage] = useState<number>(0);
  const [scrollDown, setScrollDown] = useState<boolean>(false);
  const [requestStatus, setRequestStatus] = useState<string>();
  const [filter, setFilter] = useState<string>('all');

  const { company } = useContext(CompanyContext);

  const navigate = useNavigate();
  const isMounted = useIsMounted();

  const onSearch = useCallback(async () => {
    if (!isMounted) return;
    try {
      setLoading(true);
      setError(false);
      setActiveRow(undefined);
      if (company.id !== '') {
        const response = await Company.getCompanyFlexibleRemunerationRequests(company.id);
        if (isMounted) {
          setRequests(response.requests);
          setFilteredRequests(response.requests);
        }
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [company, isMounted]);

  const handleTabChange = (status?: string) => {
    setActualPage(0);
    setPaginatorState(DEFAULT_PAGINATOR_STATE_V3);
    setRequestStatus(status);
  };

  const handleCategoryChange = (category: string) => {
    setActualPage(0);
    setPaginatorState(DEFAULT_PAGINATOR_STATE_V3);
    setFilter(category);
  };

  const onRowClick = (request: IFlexibleRemunerationRequest) => {
    TagManagerService.pushEvent(CLICK_ACTION, DETAIL_REQUESTS);
    navigate(REQUEST_DETAIL_ROUTE, { state: { request: request } });
  };

  const sortData = (columnKey: string, isAscending: boolean) => {
    sortArray(filteredRequests, columnKey, isAscending);
    paginateRequests();
  };

  const getTotalRequests = useCallback(() => {
    return filteredRequests ? filteredRequests.length : 0;
  }, [filteredRequests]);

  const paginateRequests = useCallback(() => {
    const from = actualPage * paginatorState;
    const to = from + paginatorState;
    const pageData = filteredRequests?.slice(from, to);
    setCurrentPageData(pageData || []);
    setLoading(false);
  }, [actualPage, paginatorState, filteredRequests]);

  useEffect(() => {
    const totalRequests = getTotalRequests();

    if (totalRequests) {
      paginateRequests();
    }
  }, [filteredRequests, getTotalRequests, paginateRequests]);

  useEffect(() => {
    paginateRequests();
  }, [actualPage, paginateRequests, paginatorState]);

  useEffect(() => {
    setActualPage(0);
  }, [filteredRequests, paginatorState]);

  useEffect(() => {
    const totalRequests = getTotalRequests();

    if (totalRequests) {
      paginateRequests();
    }
  }, [filteredRequests, getTotalRequests, paginateRequests]);

  useEffect(() => {
    paginateRequests();
  }, [actualPage, paginateRequests, paginatorState]);

  useEffect(() => {
    onSearch();
  }, [onSearch]);

  useEffect(() => {
    if (scrollDown) {
      window.scrollTo({ top: document.body.scrollHeight });
      setScrollDown(false);
    }
  }, [currentPageData]);

  useEffect(() => {
    let filterRequests = [...requests];
    if (requestStatus) {
      filterRequests = filterRequests.filter((requests) => requests.status === requestStatus);
    }
    if (filter !== 'all') {
      filterRequests = filterRequests.filter((request) => request.category === filter);
    }
    setFilteredRequests(filterRequests);
  }, [requestStatus, filter, requests]);

  useEffect(() => {
    setRequestStatus(undefined);
    setFilter('all');
  }, [company]);

  return (
    <>
      <RequestFilter onFilterChange={handleTabChange} onCategoryChange={handleCategoryChange} requests={requests} />
      <StyledTable
        data={currentPageData}
        columns={FLEXIBLE_REMUNERATION_REQUEST_TABLE_COLUMNS}
        error={error}
        loading={loading}
        errorAction={onSearch}
        activeRow={activeRow}
        onRowClick={onRowClick}
        sortData={sortData}
        emptyCard={{
          primaryText: TRANSLATIONS_REQUESTS.table.emptyState.title,
          secondaryText: TRANSLATIONS_REQUESTS.table.emptyState.description
        }}
      />
      {filteredRequests.length > paginatorState && filteredRequests && (
        <div className="paginator-container">
          <TablePaginationV3
            rowsPerPageOptions={DEFAULT_PAGINATOR_OPTIONS_V3}
            rowsPerPage={paginatorState}
            count={getTotalRequests()}
            actualPage={actualPage}
            onPageChange={(e) => {
              setActualPage(e);
              setScrollDown(true);
            }}
            onRowsPerPageChange={(e) => {
              setPaginatorState(e);
              setScrollDown(true);
            }}
            sectionPage={SECTION_REQUESTS}
            marginTop={20}
          />
        </div>
      )}
    </>
  );
};

export default RequestTable;
